import { FmlxCheckBox, FmlxRadioButton } from 'fmlx-common-ui';
import { ingredientNamePreferenceOptions } from './utils';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { showSnackBar } from 'utils/Global';
import RMSelect from 'components/customComponents/Controls/RMSelect';
import { ImageReportTextInput } from './ImageReportOptions';
import { generateIngredientReportPDF } from './IngredientReportPdf';
import {
  IngredientReportOptionProps,
  IngredientReportOptionsDTO,
  fontFamilies,
  fontStyles,
  registerPublicFonts,
} from './ReportsConstants';
import { useExperimentReady, useMaxIngredientsOfWell } from './hooks';
import {
  FragmentLayerDTO,
  ProteinLayerDTO,
  SeedLayerDetailsDTO,
} from 'business/models/Models';
import {
  useAllWellNavigationDetails,
  useSelectedDropImageParameters,
} from 'redux/hooks/experiment';

export const IngredientsReportOptions = forwardRef(
  (props: IngredientReportOptionProps, ref) => {
    const dropNumber = 'drop1';
    const [fitToPage, setFitToPage] = useState(false);
    const [rowLimit, setRowLimit] = useState(4);
    const [columnLimit, setColumnLimit] = useState(6);
    const rowValueRef = useRef(null);
    const columnValueRef = useRef(null);
    const [ingredientNamePreference, setIngredientNamePreference] = useState('longName');
    const allWellNavigationDetails = useAllWellNavigationDetails();
    const selectedDropImageParameters = useSelectedDropImageParameters();
    const [fontFamily, setFontFamily] = useState(
      fontFamilies.find((f) => f.key === 'Roboto') ?? fontFamilies[0],
    );
    const [fontStyle, setFontStyle] = useState('Regular');
    const [fontSize, setFontSize] = useState<number>(12);
    const [selectAllDrop, setSelectAllDrop] = useState(false);
    const [concentration, setConcentration] = useState<boolean>(true);
    const [pH, setpH] = useState<boolean>(true);
    const [pHVolumes, setpHVolumes] = useState<boolean>(true);
    const [showProteinName, setShowProteinName] = useState<boolean>(true);
    const [showFragmentName, setShowFragmentName] = useState<boolean>(true);
    const [showSeedName, setShowSeedName] = useState<boolean>(true);
    const [showAdditiveName, setShowAdditiveName] = useState<boolean>(true);
    const [showDropVolumes, setShowDropVolumes] = useState<boolean>(true);
    const [showWellVolumes, setShowWellVolumes] = useState<boolean>(true);
    const [showColumnNameHeading, setShowColumnNameHeading] = useState<boolean>(true);
    const [maxIngredientsWell, setMaxIngredientsWell] = useState<number>(null);
    const [maxIngredients, setMaxIngredients] = useState<number>(null);
    const [drops, setDrops] = useState([]);
    const [isScreenExperiment, setIsScreenExperiment] = useState<boolean>(false);
    const hasProteinLayer = useMemo(
      () =>
        props.docData.layers?.some(
          (layer) => layer.layerDetail instanceof ProteinLayerDTO,
        ) ?? false,
      [props.docData.layers],
    );
    const hasFragmentLayer = useMemo(
      () =>
        props.docData.layers?.some(
          (layer) => layer.layerDetail instanceof FragmentLayerDTO,
        ) ?? false,
      [props.docData.layers],
    );
    const hasSeedLayer = useMemo(
      () =>
        props.docData.layers?.some(
          (layer) => layer.layerDetail instanceof SeedLayerDetailsDTO,
        ) ?? false,
      [props.docData.layers],
    );
    const hasAdditiveLayer = useMemo(
      () =>
        props.docData.layers?.some(
          (layer) =>
            layer.targetDrop !== null &&
            layer.targetDrop > 0 &&
            !(
              layer.layerDetail instanceof ProteinLayerDTO ||
              layer.layerDetail instanceof FragmentLayerDTO ||
              layer.layerDetail instanceof SeedLayerDetailsDTO
            ),
        ) ?? false,
      [props.docData.layers],
    );

    useEffect(() => {
      if (!hasProteinLayer) {
        setShowProteinName(false);
      }
      if (!hasFragmentLayer) {
        setShowFragmentName(false);
      }
      if (!hasSeedLayer) {
        setShowSeedName(false);
      }
      if (!hasAdditiveLayer) {
        setShowAdditiveName(false);
      }
    }, [hasProteinLayer, hasFragmentLayer, hasSeedLayer, hasAdditiveLayer]);

    const populatingWellList = useExperimentReady(props.docData);
    useMaxIngredientsOfWell(props, setMaxIngredientsWell, setMaxIngredients);

    // Pre-load fonts on component mount, to prevent fonts loading in whenever user changed selected font
    useEffect(() => {
      if (!document.fonts) {
        return;
      }
      registerPublicFonts();
      fontFamilies.forEach((font) => {
        ['normal', 'italic'].forEach((style) => {
          ['normal', 'bold'].forEach((weight) => {
            document.fonts.load(`${style} ${weight} 16px "${font.name}"`).catch(() => {});
          });
        });
      });
    }, []);

    useEffect(() => {
      const dropsArr = [];
      if (props.docData.containerDrops) {
        props.docData.containerDrops.forEach((element) => {
          const drop = {};
          drop['name'] = 'drop' + element.dropNumber;
          drop['dropNumber'] = element.dropNumber;
          drop['checked'] = true;
          dropsArr.push(drop);
        });
        setDrops(dropsArr);
      } else {
        setIsScreenExperiment(true);
        setShowDropVolumes(false);
        setShowProteinName(false);
      }
    }, [props.docData.containerDrops]);

    useEffect(() => {
      if (selectAllDrop) {
        const tempDrops = [...drops];
        tempDrops.forEach((drop) => {
          drop.checked = true;
        });
        setDrops(tempDrops);
      }
    }, [selectAllDrop]);

    useEffect(() => {
      if (fitToPage === true) {
        setRowLimit(props.docData.containerRows);
        setColumnLimit(props.docData.containerCols);
        rowValueRef.current.value = null;
        columnValueRef.current.value = null;
      } else {
        setRowLimit(4);
        setColumnLimit(6);
      }
    }, [fitToPage]);

    useImperativeHandle(ref, () => ({
      generateAction,
    }));

    const validateOptions = async () => {
      if (
        rowLimit < 1 ||
        rowLimit > props.docData.containerRows ||
        (rowLimit === null && fitToPage !== true) ||
        columnLimit < 1 ||
        columnLimit > props.docData.containerCols ||
        (columnLimit === null && fitToPage !== true)
      ) {
        showSnackBar('Please enter valid number of rows and columns.');
        return false;
      } else if (!concentration && !pH && !pHVolumes) {
        showSnackBar('Please select Ingredient Data Option.');
        return false;
      } else if (maxIngredients > maxIngredientsWell || maxIngredients == 0) {
        showSnackBar('Please select a valid value of max ingredients');
        return false;
      } else {
        return true;
      }
    };

    async function generateAction(saveReport: boolean, onPopupBlocked?: () => void) {
      const completeDocData = await populatingWellList;
      const validationResult = await validateOptions();
      if (!validationResult) {
        return false;
      } else {
        const reportOptions: IngredientReportOptionsDTO = {
          dropNumber,
          fitToPage,
          rowLimit,
          columnLimit,
          maxIngredients,
          showProteinName,
          showFragmentName,
          showSeedName,
          showAdditiveName,
          showColumnNameHeading,
          showDropVolumes,
          showWellVolumes,
          concentration,
          pH,
          pHVolumes,
          ingredientNamePreference,
          drops,
          fontFamily,
          fontStyle,
          fontSize,
          isScreenExperiment,
        };

        await generateIngredientReportPDF(
          reportOptions,
          completeDocData,
          allWellNavigationDetails,
          selectedDropImageParameters?.dropNumber,
          saveReport,
          onPopupBlocked,
        );
        return true;
      }
    }

    function changeColumnValue(e) {
      if (!isNaN(Number(e.target.value))) {
        if (e.target.value === '') {
          setColumnLimit(null);
        } else {
          setColumnLimit(Number(e.target.value));
        }
      } else {
        setColumnLimit(null);
      }
    }
    function changeRowValue(e) {
      if (!isNaN(Number(e.target.value))) {
        if (e.target.value === '') {
          setRowLimit(null);
        } else {
          setRowLimit(Number(e.target.value));
        }
      } else {
        setRowLimit(null);
      }
    }

    function onChangeFontStyle(e) {
      const selectedStatusIndex = e.target.value;
      setFontStyle(selectedStatusIndex);
    }

    function onChangeFontFamily(e) {
      setFontFamily(fontFamilies.find((font) => font.key == e.target.value));
    }

    useEffect(() => {
      if (drops.every((drop) => drop.checked == true) && selectAllDrop !== true) {
        setSelectAllDrop(true);
      } else if (
        !drops.every((drop) => drop.checked == true) &&
        selectAllDrop !== false
      ) {
        setSelectAllDrop(false);
      }
    }, [drops]);

    const renderDropsList = useMemo(() => {
      return drops.map((drop) => (
        <>
          <div
            id={`ingredientsreportoptions-renderdroplist-wrapper-drop-${drop.dropNumber}`}
            className="flex flex-row align-items-center mt-[10px]"
          >
            <div id={`ingredientsreportoptions-checkbox-wrapper-drop-${drop.dropNumber}`}>
              <FmlxCheckBox
                id={`ingredientsreportoptions-checkbox-drop-${drop.dropNumber}`}
                checked={drop['checked']}
                onChange={(e) => {
                  const tempDrops = [...drops];
                  tempDrops.filter((x) => x['name'] === drop['name'])[0].checked =
                    e.target.checked;
                  setDrops(tempDrops);
                }}
                size={FmlxCheckBox.Size.MEDIUM}
              />
            </div>
            <label
              id={`ingredientsreportoptions-label-drop-${drop.dropNumber}`}
              style={{ fontFamily: 'Roboto' }}
              className="flex-90 text-p non-select text-[14px] ml-[10px]"
            >
              Drop {drop['dropNumber']}
            </label>
          </div>
        </>
      ));
    }, [drops]);

    const onChangeSelectAllDrop = (e) => {
      setSelectAllDrop(e.target.checked);
      setDrops((prevDrops) => {
        const updatedDrops = [...drops];
        updatedDrops.forEach((drop) => (drop.checked = e.target.checked));
        return updatedDrops;
      });
    };

    return (
      <>
        <text
          id={`ingredientsreportoptions-text-message`}
          style={{ fontFamily: 'Roboto' }}
          className=" text-[17px] text-[#5D5D5D]"
        >
          Please provide information you want to include into report.
        </text>
        <div id="ingredientsreportoptions-wrapper" className="flex flex-row mt-[10px]">
          <div
            id="ingredientsreportoptions-basic-setting-wrapper"
            className=" w-[35%] flex flex-col gap-[16px]"
            style={{ borderRight: '2px solid #CACACA' }}
          >
            <div
              id="ingredientsreportoptions-basic-drop-lists-section"
              className="flex flex-col"
            >
              <div
                id="ingredientsreportoptions-basic-drop-lists-select-all-setting"
                className="flex flex-row align-items-center "
              >
                <div id="ingredientsreportoptions-basic-drop-lists-select-all-checkbox">
                  <FmlxCheckBox
                    id="ingredientsreportoptions-basic-drop-lists-select-all-checkbox"
                    checked={drops.length > 0 && drops.every((drop) => drop.checked)}
                    indeterminate={
                      drops.some((drop) => drop.checked) &&
                      !drops.every((drop) => drop.checked)
                    }
                    onChange={(e) => onChangeSelectAllDrop(e)}
                    disabled={isScreenExperiment}
                    size={FmlxCheckBox.Size.MEDIUM}
                  />
                </div>
                <label
                  id="ingredientsreportoptions-basic-drop-lists-select-all-text"
                  style={{ fontFamily: 'Roboto' }}
                  className="flex-90 text-p non-select text-[14px] ml-[10px]"
                >
                  {' '}
                  Select All Drop{' '}
                </label>
              </div>
              <div
                id="ingredientsreportoptions-basic-drop-lists-render-section"
                className="grid grid-cols-3 ml-[25px]"
              >
                {renderDropsList}
              </div>
            </div>

            <div
              id="ingredientsreportoptions-basic-show-protein-name-setting"
              className="flex flex-row align-items-center"
            >
              <div id="ingredientsreportoptions-basic-show-protein-name-checkbox">
                <FmlxCheckBox
                  id="ingredientsreportoptions-basic-show-protein-name-checkbox"
                  checked={showProteinName}
                  onChange={(e) => setShowProteinName(e.target.checked)}
                  disabled={isScreenExperiment || !hasProteinLayer}
                  size={FmlxCheckBox.Size.MEDIUM}
                />
              </div>
              <label
                id="ingredientsreportoptions-basic-show-protein-name-text"
                style={{ fontFamily: 'Roboto' }}
                className="flex-90 text-p non-select text-[14px] ml-[10px]"
              >
                {' '}
                Show Protein Name{' '}
              </label>
            </div>

            <div
              id="ingredientsreportoptions-basic-show-fragment-name-setting"
              className="flex flex-row align-items-center"
            >
              <div id="ingredientsreportoptions-basic-show-fragment-name-checkbox">
                <FmlxCheckBox
                  id="ingredientsreportoptions-basic-show-fragment-name-checkbox"
                  checked={showFragmentName}
                  onChange={(e) => setShowFragmentName(e.target.checked)}
                  disabled={!hasFragmentLayer}
                  size={FmlxCheckBox.Size.MEDIUM}
                />
              </div>
              <label
                id="ingredientsreportoptions-basic-show-fragment-name-text"
                style={{ fontFamily: 'Roboto' }}
                className="flex-90 text-p non-select text-[14px] ml-[10px]"
              >
                Show Fragment Name
              </label>
            </div>

            <div
              id="ingredientsreportoptions-basic-show-seed-name-setting"
              className="flex flex-row align-items-center"
            >
              <div id="ingredientsreportoptions-basic-show-seed-name-checkbox">
                <FmlxCheckBox
                  id="ingredientsreportoptions-basic-show-seed-name-checkbox"
                  checked={showSeedName}
                  onChange={(e) => setShowSeedName(e.target.checked)}
                  disabled={!hasSeedLayer}
                  size={FmlxCheckBox.Size.MEDIUM}
                />
              </div>
              <label
                id="ingredientsreportoptions-basic-show-seed-name-text"
                style={{ fontFamily: 'Roboto' }}
                className="flex-90 text-p non-select text-[14px] ml-[10px]"
              >
                Show Seed Name
              </label>
            </div>

            <div
              id="ingredientsreportoptions-basic-show-additive-name-setting"
              className="flex flex-row align-items-center"
            >
              <div id="ingredientsreportoptions-basic-show-additive-name-checkbox">
                <FmlxCheckBox
                  id="ingredientsreportoptions-basic-show-additive-name-checkbox"
                  checked={showAdditiveName}
                  onChange={(e) => setShowAdditiveName(e.target.checked)}
                  disabled={!hasAdditiveLayer}
                  size={FmlxCheckBox.Size.MEDIUM}
                />
              </div>
              <label
                id="ingredientsreportoptions-basic-show-additive-name-text"
                style={{ fontFamily: 'Roboto' }}
                className="flex-90 text-p non-select text-[14px] ml-[10px]"
              >
                Show Additive Name
              </label>
            </div>

            <div
              id="ingredientsreportoptions-basic-show-drop-volumes-setting"
              className="flex flex-row align-items-center"
            >
              <div id="ingredientsreportoptions-basic-show-drop-volumes-checkbox">
                <FmlxCheckBox
                  id="ingredientsreportoptions-basic-show-drop-volumes-checkbox"
                  checked={showDropVolumes}
                  onChange={(e) => setShowDropVolumes(e.target.checked)}
                  disabled={isScreenExperiment}
                  size={FmlxCheckBox.Size.MEDIUM}
                />
              </div>
              <label
                id="ingredientsreportoptions-basic-show-drop-volumes-text"
                style={{ fontFamily: 'Roboto' }}
                className="flex-90 text-p non-select text-[14px] ml-[10px]"
              >
                Show Drop Volumes{' '}
              </label>
            </div>

            <div
              id="ingredientsreportoptions-basic-show-well-volumes-setting"
              className="flex flex-row align-items-center"
            >
              <div id="ingredientsreportoptions-basic-show-well-volumes-checkbox">
                <FmlxCheckBox
                  id="ingredientsreportoptions-basic-show-well-volumes-checkbox"
                  checked={showWellVolumes}
                  onChange={(e) => setShowWellVolumes(e.target.checked)}
                  size={FmlxCheckBox.Size.MEDIUM}
                />
              </div>
              <label
                id="ingredientsreportoptions-basic-show-well-volumes-text"
                style={{ fontFamily: 'Roboto' }}
                className="flex-90 text-p non-select text-[14px] ml-[10px]"
              >
                Show Well Volumes{' '}
              </label>
            </div>

            <div
              id="ingredientsreportoptions-basic-show-column-name-setting"
              className="flex flex-row align-items-center"
            >
              <div id="ingredientsreportoptions-basic-show-column-name-checkbox">
                <FmlxCheckBox
                  id="ingredientsreportoptions-basic-show-column-name-checkbox"
                  checked={showColumnNameHeading}
                  onChange={(e) => setShowColumnNameHeading(e.target.checked)}
                  size={FmlxCheckBox.Size.MEDIUM}
                />
              </div>
              <label
                id="ingredientsreportoptions-basic-show-column-name-text"
                style={{ fontFamily: 'Roboto' }}
                className="flex-90 text-p non-select text-[14px] ml-[10px]"
              >
                Show Column Name Heading{' '}
              </label>
            </div>
            <div
              id="ingredientsreportoptions-basic-fit-to-page-setting"
              className="flex flex-row align-items-center"
            >
              <div id="ingredientsreportoptions-basic-fit-to-page-checkbox">
                <FmlxCheckBox
                  id="ingredientsreportoptions-basic-fit-to-page-checkbox"
                  checked={fitToPage}
                  onChange={(e) => setFitToPage(e.target.checked)}
                  size={FmlxCheckBox.Size.MEDIUM}
                />
              </div>
              <label
                id="ingredientsreportoptions-basic-fit-to-page-text"
                style={{ fontFamily: 'Roboto' }}
                className="flex-90 text-p non-select text-[14px] ml-[10px]"
              >
                Fit to Page
              </label>
            </div>

            <div
              id="ingredientsreportoptions-row-column-setting"
              className="flex flex-row ml-[22px] align-middle"
            >
              <div id="ingredientsreportoptions-row-setting" className="flex flex-row">
                <label
                  id="ingredientsreportoptions-row-text"
                  style={{ fontFamily: 'Roboto' }}
                  className="flex-90 text-p non-select text-[14px]"
                >
                  Row{' '}
                </label>
                <ImageReportTextInput
                  ref={rowValueRef}
                  placeHolder={''}
                  onChange={changeRowValue}
                  value={rowLimit}
                  disabled={fitToPage}
                  inputClass={
                    'w-[50px] rounded-[2px] border-solid border-[#C2C2C2] ml-[10px]'
                  }
                />
              </div>

              <div
                id="ingredientsreportoptions-column-setting"
                className="flex flex-row ml-[18px]"
              >
                <label
                  id="ingredientsreportoptions-column-text"
                  style={{ fontFamily: 'Roboto' }}
                  className="flex-90 text-p non-select text-[14px]"
                >
                  Column{' '}
                </label>
                <ImageReportTextInput
                  ref={columnValueRef}
                  placeHolder={''}
                  onChange={changeColumnValue}
                  value={columnLimit}
                  disabled={fitToPage}
                  inputClass={
                    'w-[50px] rounded-[2px] border-solid border-[#C2C2C2] ml-[10px]'
                  }
                />
              </div>
            </div>
            <div
              id="ingredientsreportoptions-basic-max-ingredient-setting"
              className="flex flex-row"
            >
              <text
                id="ingredientsreportoptions-basic-max-ingredient-text"
                style={{ fontFamily: 'Roboto' }}
                className="text-[14px] text-[#1B1B1B]"
              >
                Max Ingredient to show
              </text>
              <ImageReportTextInput
                ref={rowValueRef}
                placeHolder={''}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '') {
                    setMaxIngredients(null);
                  } else if (!isNaN(Number(value))) {
                    setMaxIngredients(Number(value));
                  }
                }}
                value={maxIngredients}
                disabled={false}
                inputClass={
                  'w-[80px] rounded-[2px] border-solid border-[#C2C2C2] ml-[18px]'
                }
              />
            </div>
          </div>
          <div
            id="ingredientsreportoptions-ingredient-setting-wrapper"
            className=" w-[30%] flex flex-col gap-[16px] ml-[15px]"
            style={{ borderRight: '2px solid #CACACA' }}
          >
            <text
              id="ingredientsreportoptions-ingredientnamepreference-text"
              style={{ color: '#1B1B1B' }}
              className="text-[14px]"
            >
              Ingredient Name Preferences
            </text>
            <FmlxRadioButton
              id="ingredientsreportoptions-ingredientnamepreference-option"
              items={ingredientNamePreferenceOptions}
              selectedIndex={ingredientNamePreference === 'longName' ? 0 : 1}
              onChange={(e) =>
                setIngredientNamePreference(e.index === 0 ? 'longName' : 'shortName')
              }
            />
            <text
              id="ingredientsreportoptions-ingredientdataoptions-text"
              className=" text-[14px]"
              style={{ fontFamily: 'Roboto' }}
            >
              Ingredient Data Options
            </text>
            <div
              id="ingredientsreportoptions-ingredientdataoptions-setting"
              className="flex flex-row align-items-center"
            >
              <div id="ingredientsreportoptions-ingredientdataoptions-concentration-checkbox">
                <FmlxCheckBox
                  id="ingredientsreportoptions-ingredientdataoptions-concentration-checkbox"
                  checked={concentration}
                  onChange={(e) => setConcentration(e.target.checked)}
                  size={FmlxCheckBox.Size.MEDIUM}
                />
              </div>
              <label
                id="ingredientsreportoptions-ingredientdataoptions-concentration-text"
                style={{ fontFamily: 'Roboto' }}
                className="flex-90 text-p non-select text-[14px] ml-[10px]"
              >
                {' '}
                Concentration{' '}
              </label>
            </div>

            <div
              id="ingredientsreportoptions-ingredientdataoptions-ph-setting"
              className="flex flex-row align-items-center"
            >
              <div id="ingredientsreportoptions-ingredientdataoptions-ph-checkbox">
                <FmlxCheckBox
                  id="ingredientsreportoptions-ingredientdataoptions-ph-checkbox"
                  checked={pH}
                  onChange={(e) => setpH(e.target.checked)}
                  size={FmlxCheckBox.Size.MEDIUM}
                />
              </div>
              <label
                id="ingredientsreportoptions-ingredientdataoptions-ph-text"
                style={{ fontFamily: 'Roboto' }}
                className="flex-90 text-p non-select text-[14px] ml-[10px]"
              >
                {' '}
                pH{' '}
              </label>
            </div>

            <div
              id="ingredientsreportoptions-ingredientdataoptions-ph-volumes-setting"
              className="flex flex-row align-items-center"
            >
              <div id="ingredientsreportoptions-ingredientdataoptions-ph-volumes-checkbox">
                <FmlxCheckBox
                  id="ingredientsreportoptions-ingredientdataoptions-ph-volumes-checkbox"
                  checked={pHVolumes}
                  onChange={(e) => setpHVolumes(e.target.checked)}
                  size={FmlxCheckBox.Size.MEDIUM}
                />
              </div>
              <label
                id="ingredientsreportoptions-ingredientdataoptions-ph-volumes-text"
                style={{ fontFamily: 'Roboto' }}
                className="flex-90 text-p non-select text-[14px] ml-[10px]"
              >
                {' '}
                pH Volumes{' '}
              </label>
            </div>
          </div>

          <div
            id="ingredientsreportoptions-advancedfontsettings-section"
            className=" flex flex-col ml-[15px] gap-[16px] w-[250px]"
          >
            <text
              id="ingredientsreportoptions-advancedfontsettings-title"
              className="text-[14px] text-[#1B1B1B]"
            >
              Advanced Font Settings
            </text>
            <div id="ingredientsreportoptions-font-setting" className="flex flex-row">
              <label
                id="ingredientsreportoptions-font-text"
                style={{ fontFamily: 'Roboto' }}
                className="w-[40%] text-[14px]"
              >
                Font
              </label>
              <RMSelect
                style={{
                  fontFamily: 'Roboto',
                  height: '2rem',
                  width: '168px',
                  borderRadius: '2px',
                  borderColor: '#C2C2C2',
                  fontSize: '14px',
                }}
                options={fontFamilies.map((font) => {
                  return { id: font.key, name: font.name };
                })}
                selectedValue={fontFamily.key}
                onChange={(e) => onChangeFontFamily(e)}
                placeholder={'any'}
                disabled={false}
              />
            </div>

            <div
              id="ingredientsreportoptions-font-style-settings"
              className="flex flex-row"
            >
              <text
                id="ingredientsreportoptions-font-style-text"
                style={{ fontFamily: 'Roboto' }}
                className="w-[40%] text-[14px]"
              >
                Font Style
              </text>
              <RMSelect
                style={{
                  fontFamily: 'Roboto',
                  height: '2rem',
                  width: '168px',
                  borderRadius: '2px',
                  borderColor: '#C2C2C2',
                  fontSize: '14px',
                }}
                options={fontStyles.map((fontStyle) => {
                  return { id: fontStyle, name: fontStyle };
                })}
                selectedValue={fontStyle}
                onChange={(e) => onChangeFontStyle(e)}
                placeholder={'any'}
                disabled={false}
              />
            </div>

            <div
              id="ingredientsreportoptions-font-size-settings"
              className="flex flex-row align-items-center"
            >
              <text
                id="ingredientsreportoptions-font-size-text"
                style={{ fontFamily: 'Roboto' }}
                className="w-[40%] text-[14px]"
              >
                Max Font Size
              </text>
              <RMSelect
                style={{
                  fontFamily: 'Roboto',
                  height: '2rem',
                  width: '80px',
                  borderRadius: '2px',
                  borderColor: '#C2C2C2',
                  fontSize: '14px',
                }}
                options={Array.from({ length: 20 }, (_, i) => ({
                  id: String(i + 1),
                  name: String(i + 1),
                }))}
                selectedValue={String(fontSize)}
                onChange={(e) => setFontSize(Number(e.target.value))}
                placeholder={'any'}
                disabled={false}
              />
            </div>

            <text
              id="ingredientsreportoptions-font-sample-text"
              style={{ fontFamily: 'Roboto' }}
              className=" text-[14px]"
            >
              Font Sample
            </text>
            <textarea
              id="ingredientsreportoptions-font-sample-text-area"
              className="border-[#999999] h-[100px] text-[#B4B4B4] pl-[12px] w-[100%]"
              onChange={() => {}}
              disabled={false}
              readOnly={true}
              style={{
                fontFamily: fontFamily.name,
                fontSize: fontSize,
                fontWeight:
                  fontStyle == 'Bold' || fontStyle == 'Bold Italic' ? 'bold' : 'normal',
                fontStyle:
                  fontStyle == 'Italic' || fontStyle == 'Bold Italic'
                    ? 'italic'
                    : 'normal',
              }}
            >
              Sample Text
            </textarea>
          </div>
        </div>
      </>
    );
  },
);
